import { io, type Socket } from 'socket.io-client';
import { throttle, type DebouncedFunc } from 'lodash-es';
import { bugsnag } from '@/services/bugsnag';

type Channel = string;
type WebsocketHandler = (data: unknown) => void;
type ReconnectHandler = () => void;
type AcknowledgeHandler = () => void;
type SocketListener = (data: unknown, serverOffset?: number, acknowledge?: AcknowledgeHandler) => void;
type ThrottledSocketListener = DebouncedFunc<SocketListener>;
type ClientAuth = {
    serverOffset?: number;
};
type WebsocketConfig = {
    host: string;
    port: string | number;
};

let client: Socket | undefined;

const subs = new Map<Channel, Map<WebsocketHandler, ThrottledSocketListener>>();
const reconnectHandlers = new Set<ReconnectHandler>();

function getClient() {
    if (!client) {
        throw new Error('Websocket is not initialized');
    }

    return client;
}

function getClientAuth(socketClient: Socket) {
    if (!socketClient.auth || typeof socketClient.auth === 'function') {
        socketClient.auth = {};
    }

    return socketClient.auth as ClientAuth;
}

function has(channel: Channel, handler?: WebsocketHandler) {
    if (!subs.has(channel)) return false;

    return handler ? subs.get(channel)?.has(handler) ?? false : true;
}

function on(channel: Channel, handler: WebsocketHandler, timeout = 0) {
    const socketClient = getClient();
    let listeners = subs.get(channel);
    let join = false;

    if (!listeners) {
        listeners = new Map();
        subs.set(channel, listeners);

        join = true;
    }

    if (listeners.has(handler)) return;

    const listener = throttle((data, serverOffset, acknowledge) => {
        handler(data);

        const auth = getClientAuth(socketClient);

        if (serverOffset !== undefined && (!auth.serverOffset || serverOffset > auth.serverOffset)) {
            auth.serverOffset = serverOffset;
        }

        acknowledge?.();
    }, timeout, {
        trailing: timeout > 2000,
    });

    listeners.set(handler, listener);
    socketClient.on(channel, listener);

    if (join) {
        socketClient.emit('join', channel);
    }
}

function off(channel: Channel, handler?: WebsocketHandler) {
    const listeners = subs.get(channel);

    if (!listeners) return;

    const socketClient = getClient();

    if (handler) {
        const listener = listeners.get(handler);

        if (listener) {
            socketClient.off(channel, listener);

            listeners.delete(handler);
        }
    } else {
        socketClient.removeAllListeners(channel);

        listeners.clear();
    }

    if (listeners.size) return;

    subs.delete(channel);
    socketClient.emit('leave', channel);
}

function emit(channel: Channel, data: unknown, unique = false) {
    getClient().timeout(5000).emit('message', channel, JSON.stringify(data), unique);
}

function clear(...channels: Channel[]) {
    const list = channels.length ? channels : subs.keys();

    [...list].forEach(channel => off(channel));
}

function onReconnect(handler: ReconnectHandler) {
    reconnectHandlers.add(handler);
}

function offReconnect(handler: ReconnectHandler) {
    reconnectHandlers.delete(handler);
}

export const socket = {
    has,
    on,
    off,
    emit,
    clear,
    onReconnect,
    offReconnect,
};

export function init({ host, port }: WebsocketConfig) {
    const notify = (message: string, error?: Error) => {
        bugsnag.notify(error ?? new Error(message), (event: { context?: string; groupingHash?: string }) => {
            event.context = message;
            event.groupingHash = 'websocket';
        });
    };

    const socketClient = io(`wss://${host}:${port}`, {
        transports: ['websocket'],
        auth: {
            serverOffset: undefined,
        },
        withCredentials: true,
        ackTimeout: 5000,
        retries: 3,
    });

    client = socketClient;

    socketClient.io.on('reconnect_error', error => notify('reconnect_error', error));
    socketClient.io.on('reconnect_failed', () => notify('reconnect_failed'));

    socketClient.io.on('reconnect', () => {
        [...subs.keys()].forEach((channel) => {
            socketClient.emit('join', channel);
        });

        reconnectHandlers.forEach(handler => handler());
    });
}
